/**
 * WorkshopNewsletterBand — slim, full-width "The Weekly Load" signup band for
 * the Workshop category + trailer-type collection pages. Replaces the old
 * "Not sure which X you need? / Read the owner guides" callout (removed
 * 2026-10-12 — we don't publish spec/fitment guides, too many trailer
 * variables to claim that responsibly).
 *
 * Slimmer than the FAQ SectionNewsletter: single full-width horizontal band
 * (logo + headline on the left, renter/owner toggle + email + CTA on the
 * right) so visitors can still self-select renter vs owner. Real working form:
 * POSTs to /api/newsletter/subscribe with { email, audience, source }.
 * Self-contained inline styles (same Weekly Load visual family — warm paper
 * card, condensed headline, navy accents, no emoji).
 */

import React, { useState } from 'react';

import logoImg from '../../assets/newsletter/weekly-load-logo.png';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const C = {
  paper: '#F6F4EF',
  navy: '#0E1B2A',
  blue: '#2F7FE0',
  red: '#B02419',
  ink: '#1A1A1A',
  muted: '#5A6B7A',
  green: '#2E9E6B',
};

const CONDENSED =
  "'Oswald', 'Archivo Narrow', 'Arial Narrow', 'Franklin Gothic Medium', Impact, system-ui, sans-serif";

const TrailerIcon = ({ color = '#fff', size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="8" width="15" height="7" rx="1" />
    <path d="M17 12h5" />
    <circle cx="7" cy="17.5" r="1.8" />
    <circle cx="12.5" cy="17.5" r="1.8" />
  </svg>
);

const TagIcon = ({ color = '#fff', size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.6 13.4L12 22l-8.6-8.6a2 2 0 0 1-.6-1.4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 1.4.6l6.4 6.4a2 2 0 0 1 0 2.8z" transform="rotate(90 12 12)" />
    <circle cx="9" cy="9" r="1.6" fill={color} stroke="none" />
  </svg>
);

const CheckCircleIcon = ({ color = C.green, size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <circle cx="12" cy="12" r="11" />
    <path d="M7 12.5l3.2 3.2L17 9" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

const WorkshopNewsletterBand = ({ source = 'weekly-load-workshop' }) => {
  const [role, setRole] = useState('renter'); // 'renter' | 'owner'
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const emailInvalid = touched && !EMAIL_RE.test(email.trim());
  const accent = role === 'owner' ? C.red : C.blue;

  const submit = async e => {
    e.preventDefault();
    setTouched(true);
    const clean = email.trim();
    if (!EMAIL_RE.test(clean)) {
      setError('Please enter a valid email.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: clean,
          audience: role,
          source,
          list: 'bigloads-weekly-load',
        }),
      });
      if (res.ok || res.status === 202) {
        setDone(true);
      } else if (res.status === 400) {
        setError('Please enter a valid email.');
      } else {
        setError("Hmm, that didn't go through. Try again in a moment.");
      }
    } catch (err) {
      setError("Couldn't reach the server. Give it another shot.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.band} className="wlb-band">
      {done ? (
        <div style={styles.doneRow}>
          <CheckCircleIcon color={C.green} size={38} />
          <div>
            <div style={styles.doneHead}>YOU'RE ON THE LIST</div>
            <div style={styles.doneSub}>
              Look out for your first <strong>Weekly Load</strong> soon — trailer tips,
              deals, and listings near you.
            </div>
          </div>
        </div>
      ) : (
        <>
          <div style={styles.left} className="wlb-left">
            <img src={logoImg} alt="The Weekly Load" style={styles.logo} />
            <div>
              <div style={styles.head}>
                <span style={{ color: C.ink }}>GET THE </span>
                <span style={{ color: C.red }}>WEEKLY LOAD</span>
              </div>
              <div style={styles.sub}>
                The best trailer tips, deals, and listings near you. Once a week, no spam.
              </div>
            </div>
          </div>

          <form style={styles.right} className="wlb-right" onSubmit={submit} noValidate>
            <div style={styles.roleRow} className="wlb-roles">
              <button
                type="button"
                onClick={() => setRole('renter')}
                style={{
                  ...styles.roleBtn,
                  ...(role === 'renter'
                    ? { background: C.blue, borderColor: C.blue, color: '#fff' }
                    : styles.roleIdle),
                }}
              >
                <TrailerIcon color={role === 'renter' ? '#fff' : C.muted} />
                I'M RENTING
              </button>
              <button
                type="button"
                onClick={() => setRole('owner')}
                style={{
                  ...styles.roleBtn,
                  ...(role === 'owner'
                    ? { background: C.red, borderColor: C.red, color: '#fff' }
                    : styles.roleIdle),
                }}
              >
                <TagIcon color={role === 'owner' ? '#fff' : C.muted} />
                I OWN / WANT TO LIST
              </button>
            </div>
            <div style={styles.inputRow} className="wlb-input-row">
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="Enter your email address"
                value={email}
                disabled={submitting}
                onChange={e => {
                  setEmail(e.target.value);
                  if (error) setError('');
                }}
                onBlur={() => setTouched(true)}
                style={{
                  ...styles.input,
                  ...(emailInvalid || error ? styles.inputError : {}),
                }}
                aria-label="Email address"
                aria-invalid={emailInvalid || !!error}
              />
              <button
                type="submit"
                style={{ ...styles.cta, background: accent }}
                disabled={submitting}
              >
                {submitting ? 'SENDING…' : 'SEND IT MY WAY'}
              </button>
            </div>
            {error ? <div style={styles.errorText}>{error}</div> : null}
          </form>
        </>
      )}

      <style>{`
        @media (max-width: 860px) {
          .wlb-band { flex-direction: column !important; align-items: stretch !important; gap: 16px !important; }
          .wlb-left { justify-content: center !important; text-align: left !important; }
          .wlb-right { width: 100% !important; }
        }
        @media (max-width: 560px) {
          .wlb-roles { flex-direction: column !important; }
          .wlb-input-row { flex-direction: column !important; }
        }
      `}</style>
    </div>
  );
};

const styles = {
  band: {
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '28px',
    flexWrap: 'wrap',
    background: C.paper,
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: '16px',
    padding: '20px 24px',
    boxShadow: '0 8px 26px rgba(0,0,0,0.08)',
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  },
  left: { display: 'flex', alignItems: 'center', gap: '16px', flex: '1 1 320px', minWidth: 0 },
  logo: { width: '120px', height: 'auto', display: 'block', flex: '0 0 auto' },
  head: {
    fontFamily: CONDENSED,
    fontWeight: 700,
    fontSize: '24px',
    lineHeight: 1.05,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  sub: { fontSize: '13.5px', lineHeight: 1.45, color: C.muted, fontWeight: 500, marginTop: '4px', maxWidth: '360px' },
  right: { display: 'flex', flexDirection: 'column', gap: '10px', flex: '1 1 360px', minWidth: 0 },
  roleRow: { display: 'flex', gap: '8px' },
  roleBtn: {
    flex: '1 1 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '7px',
    padding: '9px 10px',
    borderRadius: '9px',
    border: '2px solid',
    fontFamily: CONDENSED,
    fontSize: '13.5px',
    fontWeight: 700,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    cursor: 'pointer',
    transition: 'all 0.12s ease',
    whiteSpace: 'nowrap',
  },
  roleIdle: { background: '#fff', borderColor: '#C9C4B8', color: C.muted },
  inputRow: { display: 'flex', gap: '8px' },
  input: {
    flex: '1 1 auto',
    minWidth: 0,
    boxSizing: 'border-box',
    background: '#fff',
    color: C.ink,
    border: '1px solid #C9C4B8',
    borderRadius: '8px',
    padding: '12px 14px',
    fontSize: '15px',
    fontFamily: 'inherit',
    outline: 'none',
  },
  inputError: { border: `1px solid ${C.red}`, background: 'rgba(176,36,25,0.05)' },
  cta: {
    flex: '0 0 auto',
    padding: '12px 20px',
    borderRadius: '8px',
    border: 'none',
    color: '#fff',
    fontFamily: CONDENSED,
    fontSize: '14.5px',
    fontWeight: 700,
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    cursor: 'pointer',
    boxShadow: '0 6px 16px rgba(0,0,0,0.16)',
    whiteSpace: 'nowrap',
  },
  errorText: { color: C.red, fontSize: '12.5px', margin: '2px 2px 0', fontWeight: 600 },
  doneRow: { display: 'flex', alignItems: 'center', gap: '16px', width: '100%' },
  doneHead: {
    fontFamily: CONDENSED,
    fontWeight: 700,
    fontSize: '20px',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: C.ink,
  },
  doneSub: { fontSize: '13.5px', lineHeight: 1.45, color: C.muted, marginTop: '2px' },
};

export default WorkshopNewsletterBand;
